/**
 * The vendor console's routes (`vendor.ts`; `routes.ts` spreads these into
 * `ROUTES`), plus the two announcement reads outside the vendor tenant.
 *
 * Every `/vendor/*` route is the vendor tenant's only, tenant-wide:
 * `vendor.ops` reads, `vendor.admin` writes (D27). `vendor.*` grants exist
 * only in the vendor tenant (`apps/cloud-api/src/auth/grants.ts`), so a
 * tenant admin is refused `forbidden` at the gate; the handlers also demand
 * a `VendorAccess` proof (`apps/cloud-api/src/tenancy/vendorRepo.ts`).
 * Every write is recorded in the vendor's activity log.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import type { RouteDef } from './routes.js';
import {
  announcementPathSchema,
  announcementResponseSchema,
  createAnnouncementRequestSchema,
  listAnnouncementsResponseSchema,
  listPublicAnnouncementsResponseSchema,
  listVendorActivityQuerySchema,
  listVendorActivityResponseSchema,
  listVendorAnnouncementsResponseSchema,
  listVendorTenantsResponseSchema,
  pinResponseSchema,
  reactivateTenantRequestSchema,
  setPinRequestSchema,
  sitePinPathSchema,
  suspendTenantRequestSchema,
  tenantPathSchema,
  updateAnnouncementRequestSchema,
  vendorOpsResponseSchema,
  vendorReleasesResponseSchema,
  vendorTenantDetailResponseSchema,
  vendorTenantResponseSchema,
} from './vendor.js';

const VENDOR_ONLY = 'Vendor tenant only.';

export const VENDOR_ROUTES = {
  listVendorTenants: {
    id: 'listVendorTenants',
    method: 'GET',
    path: '/vendor/tenants',
    tag: 'vendor',
    summary: 'Tenants, with their status and counts',
    description: `Every tenant of this regional deployment (D12) with its status and counts: users, sites, active nodes, live productions, and cloud-production hours this month. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: listVendorTenantsResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  getVendorTenant: {
    id: 'getVendorTenant',
    method: 'GET',
    path: '/vendor/tenants/{tenantId}',
    tag: 'vendor',
    summary: 'One tenant, in detail',
    description: `The tenant with its counts, sites, nodes, live productions and current alarms. ${VENDOR_ONLY}`,
    params: tenantPathSchema,
    query: null,
    body: null,
    response: vendorTenantDetailResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  suspendTenant: {
    id: 'suspendTenant',
    method: 'POST',
    path: '/vendor/tenants/{tenantId}/suspend',
    tag: 'vendor',
    summary: 'Suspend a tenant',
    description:
      'Refuses the tenant’s users (every route answers `suspended`, within the authorizer’s five-minute cache) and its nodes’ site links, with `reason`. Nodes keep running their productions locally. Suspending a suspended tenant replaces the reason. The vendor tenant cannot be suspended (`conflict`). Vendor tenant only.',
    params: tenantPathSchema,
    query: null,
    body: suspendTenantRequestSchema,
    response: vendorTenantResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  reactivateTenant: {
    id: 'reactivateTenant',
    method: 'POST',
    path: '/vendor/tenants/{tenantId}/reactivate',
    tag: 'vendor',
    summary: 'Reactivate a suspended tenant',
    description: `Idempotent: an active tenant is returned as it is. \`note\` goes to the vendor's activity log. ${VENDOR_ONLY}`,
    params: tenantPathSchema,
    query: null,
    body: reactivateTenantRequestSchema,
    response: vendorTenantResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  getVendorOps: {
    id: 'getVendorOps',
    method: 'GET',
    path: '/vendor/ops',
    tag: 'vendor',
    summary: 'The ops dashboard',
    description: `Every node of every tenant (role, link, witness, packages, certificate, clock, last seen), every live production with its protection, and the alarms evaluated on the latest status (D27). ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorOpsResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  getVendorReleases: {
    id: 'getVendorReleases',
    method: 'GET',
    path: '/vendor/releases',
    tag: 'vendor',
    summary: 'Releases, pins and the rollout',
    description: `The latest release, the recently published ones, the stage and site pins (D15), and for every node the release it is served and the one it holds. \`unavailable\` on a stack without a release store. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: vendorReleasesResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  setStagePin: {
    id: 'setStagePin',
    method: 'PUT',
    path: '/vendor/pins/stage',
    tag: 'vendor',
    summary: 'Pin the whole stage to a release',
    description: `Every site without a site pin is served \`listHash\` instead of the latest (D15: a hold or a rollback). The list must be published (\`validation-failed\`). ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: setPinRequestSchema,
    response: pinResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  clearStagePin: {
    id: 'clearStagePin',
    method: 'DELETE',
    path: '/vendor/pins/stage',
    tag: 'vendor',
    summary: 'Clear the stage pin',
    description: `Idempotent; answers \`{ pin: null }\`. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: pinResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  setSitePin: {
    id: 'setSitePin',
    method: 'PUT',
    path: '/vendor/pins/sites/{siteId}',
    tag: 'vendor',
    summary: 'Pin one site to a release',
    description: `The site's nodes are served \`listHash\` whatever the stage pin says. The site must exist in some tenant (\`not-found\`); the cloud site cannot be pinned (its tasks run their production's release, PLAN §9.2 — \`validation-failed\`), nor to an unpublished list. ${VENDOR_ONLY}`,
    params: sitePinPathSchema,
    query: null,
    body: setPinRequestSchema,
    response: pinResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  clearSitePin: {
    id: 'clearSitePin',
    method: 'DELETE',
    path: '/vendor/pins/sites/{siteId}',
    tag: 'vendor',
    summary: 'Clear a site pin',
    description: `Idempotent; answers \`{ pin: null }\`. ${VENDOR_ONLY}`,
    params: sitePinPathSchema,
    query: null,
    body: null,
    response: pinResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  listVendorActivity: {
    id: 'listVendorActivity',
    method: 'GET',
    path: '/vendor/activity',
    tag: 'vendor',
    summary: 'The vendor’s own actions',
    description: `Every vendor write — tenants created, suspended and reactivated, pins set and cleared, announcements — newest first, with who did it. Kept one year. ${VENDOR_ONLY}`,
    params: null,
    query: listVendorActivityQuerySchema,
    body: null,
    response: listVendorActivityResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  listVendorAnnouncements: {
    id: 'listVendorAnnouncements',
    method: 'GET',
    path: '/vendor/announcements',
    tag: 'vendor',
    summary: 'Every announcement',
    description: `Past, current and scheduled, newest start first. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: null,
    response: listVendorAnnouncementsResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: [],
  },
  createAnnouncement: {
    id: 'createAnnouncement',
    method: 'POST',
    path: '/vendor/announcements',
    tag: 'vendor',
    summary: 'Create an announcement or incident',
    description: `Shown in-app to the tenants it targets (\`tenantIds\`, or every tenant when null) while in force, live over the change channel; a public one (every tenant) also on the status page. ${VENDOR_ONLY}`,
    params: null,
    query: null,
    body: createAnnouncementRequestSchema,
    response: announcementResponseSchema,
    successStatus: 201,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  getVendorAnnouncement: {
    id: 'getVendorAnnouncement',
    method: 'GET',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'One announcement',
    description: VENDOR_ONLY,
    params: announcementPathSchema,
    query: null,
    body: null,
    response: announcementResponseSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  updateAnnouncement: {
    id: 'updateAnnouncement',
    method: 'PATCH',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'Edit an announcement',
    description: `Changes any of its fields; \`version\` is the one the edit was made from (\`conflict\` if it moved — D35's overwrite-or-discard). Ending an incident is setting \`endsAt\`. ${VENDOR_ONLY}`,
    params: announcementPathSchema,
    query: null,
    body: updateAnnouncementRequestSchema,
    response: announcementResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteAnnouncement: {
    id: 'deleteAnnouncement',
    method: 'DELETE',
    path: '/vendor/announcements/{announcementId}',
    tag: 'vendor',
    summary: 'Delete an announcement',
    description: `Gone from every tenant and the status page; returns what was deleted. ${VENDOR_ONLY}`,
    params: announcementPathSchema,
    query: null,
    body: null,
    response: announcementResponseSchema,
    successStatus: 200,
    permission: 'vendor.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  listAnnouncements: {
    id: 'listAnnouncements',
    method: 'GET',
    path: '/announcements',
    tag: 'tenancy',
    summary: 'Announcements for my tenant',
    description: 'The vendor’s announcements in force now that target the caller’s tenant: the in-app banner. Any signed-in user.',
    params: null,
    query: null,
    body: null,
    response: listAnnouncementsResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
  listPublicAnnouncements: {
    id: 'listPublicAnnouncements',
    method: 'GET',
    path: '/public/announcements',
    tag: 'status',
    summary: 'Public announcements (the status page)',
    description:
      'Public announcements in force, scheduled, or ended within the last seven days, newest start first — what the status page shows. No credential: the status page’s synthetic check reads it.',
    params: null,
    query: null,
    body: null,
    response: listPublicAnnouncementsResponseSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'none',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
